// Coleções — o que o cliente vê.
//
// Também não existia na PoC, porque lá não havia versionamento: publicar era
// copiar staging para produção. Aqui a coleção **aponta** para uma versão
// selada, e a tela mostra as três coisas que respondem "posso entregar esta
// URL ao cliente?" na mesma linha: versão vigente, licença e atribuição.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { alterar, enviar, obter, type Camada, type Colecao } from "../api.js";
import { Carregando, Falha, quando, Vazio } from "../pecas/basicos.js";
import { Busca, casa } from "../pecas/Busca.js";
import { Confirmar } from "../pecas/Confirmar.js";
import { EditarColecao } from "./EditarColecao.js";
import { NovaColecao } from "./NovaColecao.js";
import { Publicar } from "./Publicar.js";

export function Colecoes({
  avisar,
}: {
  avisar: (texto: string, erro?: boolean) => void;
}): ReactNode {
  const [publicando, setPublicando] = useState<Colecao | null>(null);
  const [criando, setCriando] = useState(false);
  const [despublicando, setDespublicando] = useState<Colecao | null>(null);
  const [renomeando, setRenomeando] = useState<Colecao | null>(null);
  const [busca, setBusca] = useState("");
  const qc = useQueryClient();
  const camadas = useQuery({
    queryKey: ["camadas", null],
    queryFn: () => obter<Camada[]>("/camadas"),
    enabled: criando,
  });
  const despublicar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      enviar<void>(`/colecoes/${id}/despublicar`, { motivo: motivo || null }),
    onSuccess: () => {
      avisar("Coleção despublicada. A URL deixou de responder — republicar a devolve.");
      setDespublicando(null);
      void qc.invalidateQueries({ queryKey: ["colecoes"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const renomear = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      alterar<Colecao>(`/colecoes/${id}`, corpo),
    onSuccess: () => {
      avisar("Coleção salva. O identificador público não muda — ele é contrato.");
      setRenomeando(null);
      void qc.invalidateQueries({ queryKey: ["colecoes"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviar<Colecao>("/colecoes", corpo),
    onSuccess: () => {
      avisar("Coleção criada. Ela ainda não serve nada: publique uma versão.");
      setCriando(false);
      void qc.invalidateQueries({ queryKey: ["colecoes"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });
  const c = useQuery({ queryKey: ["colecoes"], queryFn: () => obter<Colecao[]>("/colecoes") });
  if (c.isLoading) return <Carregando />;
  if (c.error) return <Falha erro={c.error} />;
  const todas = c.data ?? [];
  const lista = todas.filter((x) => casa(`${x.id_publico} ${x.titulo} ${x.camada}`, busca));

  const cabecalho = (
    <div className="barra-tela">
      <Busca
        valor={busca}
        aoMudar={setBusca}
        rotulo="Buscar coleção ou camada"
        achados={lista.length}
        total={todas.length}
      />
      <button className="btn btn-primario" onClick={() => setCriando(true)}>
        Nova coleção…
      </button>
    </div>
  );
  const gaveta = criando ? (
    <NovaColecao
      camadas={camadas.data ?? []}
      aoFechar={() => setCriando(false)}
      aoSalvar={(corpo) => criar.mutate(corpo)}
      salvando={criar.isPending}
    />
  ) : null;

  if (lista.length === 0) {
    return (
      <>
        {cabecalho}
        {gaveta}
        <Vazio>
          Nenhuma coleção. Uma coleção é o que o cliente consome: ela aponta para uma camada e,
          quando publicada, para uma versão selada dela.
        </Vazio>
      </>
    );
  }

  return (
    <div className="rolagem">
      {cabecalho}
      {gaveta}
      <table className="tabela">
        <thead>
          <tr>
            <th>Identificador público</th>
            <th>Camada</th>
            <th>Versão</th>
            <th>Feições</th>
            <th>Procedência</th>
            <th>Graça</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lista.map((x) => (
            <tr key={x.id_publico}>
              <td>
                {/* O id_publico é contrato: uma vez que o analista salvou a URL
                  no projeto de ArcGIS dele, mudá-la quebra o projeto. */}
                <code className="mono">{x.id_publico}</code>
                <div className="discreto">{x.titulo}</div>
              </td>
              <td className="discreto">{x.camada}</td>
              <td className="mono">
                {x.versao_vigente ?? <span style={{ color: "var(--agd)" }}>nenhuma</span>}
                {x.versao_anterior ? (
                  <div className="discreto">anterior: {x.versao_anterior}</div>
                ) : null}
              </td>
              <td className="mono">
                {x.total_feicoes ?? "—"}
                {/* Q5, SPEC §12. O aviso vive AQUI, ao lado da contagem, porque
                    é a contagem que engana: meio milhão de pontos é leve e
                    cinco mil polígonos de CAR são cem megabytes. E o modo de
                    falha não dá erro — o cliente trunca em silêncio e desenha
                    um mapa incompleto. */}
                {x.avisa_geojson && x.bytes_por_pagina !== null ? (
                  <div
                    className="discreto"
                    style={{ color: "var(--agd)" }}
                    title="O cliente pede 5.000 feições por página. Nesta camada isso é uma resposta grande demais — use os vector tiles (MVT)."
                  >
                    ~{Math.round(x.bytes_por_pagina / 1048576)} MB por página em GeoJSON — prefira
                    MVT
                  </div>
                ) : null}
              </td>
              <td className="discreto">
                {x.licenca ? (
                  <>
                    {x.licenca}
                    <div>{x.atribuicao}</div>
                  </>
                ) : (
                  <span style={{ color: "var(--agd)" }}>fonte sem licença</span>
                )}
              </td>
              <td className="mono discreto">{x.graca_ate ? quando(x.graca_ate) : "—"}</td>
              <td>
                {/* Publicar é a única operação do back office com efeito
                    externo imediato — muda o que o cliente vê. Ficava só na
                    API, e quem operava montava a requisição à mão. */}
                {/* As reticências dizem que abre uma escolha, e não que
                    publica ao clicar — com a gaveta aberta havia dois botões
                    "Publicar" na tela ao mesmo tempo. */}
                <div className="linha-botoes">
                  <button className="btn btn-pequeno" onClick={() => setPublicando(x)}>
                    Publicar…
                  </button>
                  <button className="btn btn-pequeno" onClick={() => setRenomeando(x)}>
                    Editar
                  </button>
                  {/* Despublicar só aparece quando há o que despublicar: uma
                      coleção sem versão vigente já não serve nada. */}
                  {x.versao_vigente !== null ? (
                    <button
                      className="btn btn-pequeno btn-perigo"
                      onClick={() => setDespublicando(x)}
                    >
                      Despublicar
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {despublicando ? (
        <Confirmar
          titulo="Despublicar coleção"
          rotuloAcao="Despublicar"
          perigo
          confirmacao={despublicando.id_publico}
          descricao={
            <>
              <p>
                A URL{" "}
                <code className="mono">/ogc/collections/{despublicando.id_publico}/items</code>{" "}
                <b>deixa de responder</b>. Quem a salvou num projeto de ArcGIS descobre no meio de
                uma análise — não há aviso.
              </p>
              <p>
                Nada é apagado: a coleção continua existindo com o mesmo identificador, e publicar
                de novo devolve o endereço.
              </p>
            </>
          }
          aoConfirmar={(motivo) => despublicar.mutate({ id: despublicando.id_publico, motivo })}
          aoCancelar={() => setDespublicando(null)}
          pendente={despublicar.isPending}
        />
      ) : null}

      {renomeando ? (
        <EditarColecao
          colecao={renomeando}
          aoFechar={() => setRenomeando(null)}
          aoSalvar={(corpo) => renomear.mutate({ id: renomeando.id_publico, corpo })}
          salvando={renomear.isPending}
        />
      ) : null}

      {publicando ? (
        <Publicar colecao={publicando} avisar={avisar} aoFechar={() => setPublicando(null)} />
      ) : null}
    </div>
  );
}
